import { DateTime } from "luxon";

import { BannerItemType, BannerScreensAvailableType } from "../../types/common";
import { Dev } from "./consoleUtil";

type AnyObject = {
  [key: string]: any;
};

const UtilFunctions = {
  formatBytes: (
    bytes: number,
    toFixed = 1,
    greatness: string[] | "KB" | "MB" = ["B", "KB", "MB", "GB"]
  ) => {
    if (Array.isArray(greatness)) {
      if (!+bytes) return "0 B";

      const sizeMagnitude = 1024;
      const decimalFixed = toFixed < 0 ? 0 : toFixed;
      const sizeNames = greatness;

      const sizePower = Math.floor(Math.log(bytes) / Math.log(sizeMagnitude));

      const calculatedValue = parseFloat(
        (bytes / Math.pow(sizeMagnitude, sizePower)).toFixed(decimalFixed)
      );

      const calculatedSize = sizeNames[sizePower];

      return `${calculatedValue} ${calculatedSize}`;
    } else if (greatness === "KB") {
      return (bytes / 1048576).toFixed(toFixed) + " KB";
    } else if (greatness === "MB") {
      return (bytes / 1048576).toFixed(toFixed) + " MB";
    } else {
      return (bytes / 1073741824).toFixed(toFixed) + " GB";
    }
  },

  // Based on: https://javascript.info/fetch-progress
  getDownloadSize: async (
    urls: string,
    setter: React.Dispatch<React.SetStateAction<number>>,
    returnType: "blob" | "text" | "json" = "blob"
  ): Promise<Blob | string | object> => {
    try {
      const response = await fetch(urls);
      const streamReader = (response.body as ReadableStream<Uint8Array>).getReader();

      // This value may be wrong.
      const contentLength = +(response.headers.get("Content-Length") as string);

      let receivedLength = 0;
      let binaryChunks: Uint8Array[] = [];

      let stateUpdateIterationsLimit = 30;
      let stateUpdateIterations = stateUpdateIterationsLimit;

      while (true) {
        const { done, value } = await streamReader.read();

        if (done) break;

        binaryChunks.push(value);
        receivedLength += value.length;

        stateUpdateIterations++;
        if (stateUpdateIterations >= stateUpdateIterationsLimit) {
          setter(receivedLength);
          stateUpdateIterations = 0;
        }
      }

      const rawFileData = new Blob(binaryChunks);
      if (returnType === "blob") return rawFileData;

      const returnTextFileAproach = () => {
        const fileObject = new File([rawFileData], "downloadedFile");
        return fileObject.text();
      };

      const returnTextUintArrayAproach = () => {
        let binaryChunksJoin = new Uint8Array(receivedLength);
        let position = 0;
        for (let binaryChunk of binaryChunks) {
          binaryChunksJoin.set(binaryChunk, position);
          position += binaryChunk.length;
        }
        let decodedText = new TextDecoder("utf-8").decode(binaryChunksJoin);
        return decodedText;
      };

      const dataAsString = await returnTextFileAproach();

      if (returnType === "json") return await JSON.parse(dataAsString);

      return dataAsString;
    } catch (error) {
      console.log(...new Dev("getDownloadSize", error).Error());
      throw new Error("Erro ao calcular tamanho dos dados");
    }
  },

  sortObjectArrayBy: (
    objectoToSort: AnyObject[],
    keys: string[] | string,
    asc: boolean = false
  ) => {
    const compareFunction = (a: AnyObject, b: AnyObject) => {
      if (typeof keys === "string") {
        if (a[keys] < b[keys]) return asc ? -1 : 1;
        if (a[keys] > b[keys]) return asc ? 1 : -1;
        return 0;
      } else {
        const keysValueA = `${a[keys[0]]}${a[keys[1]]}`;

        const keysValueB = `${b[keys[0]]}${b[keys[1]]}`;

        if (keysValueA < keysValueB) return asc ? -1 : 1;
        if (keysValueA > keysValueB) return asc ? 1 : -1;
        return 0;
      }
    };

    return objectoToSort.sort(compareFunction);
  },

  isTwoArraysEqual: (firstArray: AnyObject[], secondArray: AnyObject[]) => {
    return (
      firstArray.length === secondArray.length &&
      firstArray.every((firstElement) =>
        secondArray.some(
          (secondElement) =>
            firstElement.student_name === secondElement.student_name &&
            firstElement.student_age === secondElement.student_age
        )
      )
    );
  },

  arrayMonth: [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ],

  arrayweek: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],

  getFormatedDateString: (selectedDate: string, selectedTime: string) => {
    const dateBase = DateTime.fromISO(selectedDate).set({
      hour: parseInt(selectedTime.split(":")[0]),
      minute: parseInt(selectedTime.split(":")[1]),
    });

    const dayString = dateBase.setLocale("pt-br").toFormat("dd");
    const monthString = dateBase.setLocale("pt-br").toFormat("LLLL");
    const monthStringFormated = monthString.charAt(0).toUpperCase() + monthString.slice(1, 3);
    const timeString = dateBase.setLocale("pt-br").toFormat("HH:mm");

    return `${
      UtilFunctions.arrayweek[dateBase.weekday]
    }, ${dayString} de ${monthStringFormated} às ${timeString}`;
  },

  isValidUrl: (inputUrl: string) => {
    if (/^[A-Za-z0-9]*$/.test(inputUrl) && inputUrl.length > 4) {
      return true;
    }
    return false;
  },

  colorList: [
    "Aqua",
    "Aquamarine",
    "Bisque",
    "Black",
    "Blue",
    "BlueViolet",
    "Brown",
    "BurlyWood",
    "CadetBlue",
    "Chartreuse",
    "Chocolate",
    "Coral",
    "CornflowerBlue",
    "Crimson",
    "Cyan",
    "DarkBlue",
    "DarkCyan",
    "DarkGoldenRod",
    "DarkGray",
    "DarkGreen",
    "DarkKhaki",
    "DarkMagenta",
    "DarkOliveGreen",
    "Darkorange",
    "DarkOrchid",
    "DarkRed",
    "DarkSalmon",
    "DarkSeaGreen",
    "DarkSlateBlue",
    "DarkSlateGrey",
    "DarkTurquoise",
    "DarkViolet",
    "DeepPink",
    "DeepSkyBlue",
    "DimGray",
    "DodgerBlue",
    "FireBrick",
    "ForestGreen",
    "Fuchsia",
    "Gold",
    "GoldenRod",
    "Grey",
    "Green",
    "GreenYellow",
    "HotPink",
    "IndianRed",
    "Indigo",
    "Khaki",
    "LawnGreen",
    "LightBlue",
    "LightCoral",
    "LightGrey",
    "LightGreen",
    "LightPink",
    "LightSalmon",
    "LightSeaGreen",
    "LightSkyBlue",
    "LightSlateGrey",
    "LightSteelBlue",
    "Lime",
    "LimeGreen",
    "Magenta",
    "Maroon",
    "MediumAquaMarine",
    "MediumBlue",
    "MediumOrchid",
    "MediumPurple",
    "MediumSeaGreen",
    "MediumSlateBlue",
    "MediumSpringGreen",
    "MediumTurquoise",
    "MediumVioletRed",
    "MidnightBlue",
    "NavajoWhite",
    "Navy",
    "Olive",
    "OliveDrab",
    "Orange",
    "OrangeRed",
    "Orchid",
    "PaleGoldenRod",
    "PaleGreen",
    "PaleTurquoise",
    "PaleVioletRed",
    "PeachPuff",
    "Peru",
    "Pink",
    "Plum",
    "PowderBlue",
    "Purple",
    "Red",
    "RosyBrown",
    "RoyalBlue",
    "SaddleBrown",
    "Salmon",
    "SandyBrown",
    "SeaGreen",
    "Sienna",
    "Silver",
    "SkyBlue",
    "SlateBlue",
    "SlateGrey",
    "SpringGreen",
    "SteelBlue",
    "Tan",
    "Teal",
    "Thistle",
    "Tomato",
    "Turquoise",
    "Violet",
    "Wheat",
    "Yellow",
    "YellowGreen",
  ],

  randomColor: () => {
    return UtilFunctions.colorList[Math.floor(Math.random() * 117)];
  },

  availableCA: [
    "CERES - DEPARTAMENTO DE GEOGRAFIA",
    "CERES - DEPARTAMENTO DE HISTÓRIA",
    "CERES - DEPTO CIÊNCIAS EXATAS E APLICADAS",
    "COORDENAÇÃO DO CURSO DE ARTES VISUAIS",
    "COORDENAÇÃO DO CURSO DE FÍSICA - A DISTÂNCIA",
    "COORDENAÇÃO DO CURSO DE MEDICINA",
    "DEPARTAMENTO DE ADMINISTRAÇÃO PUBLICA E GESTÃO SOCIAL",
    "DEPARTAMENTO DE ANTROPOLOGIA",
    "DEPARTAMENTO DE ANÁLISES CLÍNICAS E TOXICOLÓGICAS",
    "DEPARTAMENTO DE ARQUITETURA",
    "DEPARTAMENTO DE ARTES",
    "DEPARTAMENTO DE BIOFÍSICA E FARMACOLOGIA",
    "DEPARTAMENTO DE BIOLOGIA CELULAR E GENÉTICA",
    "DEPARTAMENTO DE BIOQUÍMICA",
    "DEPARTAMENTO DE BOTÂNICA E ZOOLOGIA",
    "DEPARTAMENTO DE CIRURGIA",
    "DEPARTAMENTO DE CIÊNCIA DA INFORMAÇÃO",
    "DEPARTAMENTO DE CIÊNCIAS ADMINISTRATIVAS - DEPAD",
    "DEPARTAMENTO DE CIÊNCIAS ATMOSFÉRICAS E CLIMÁTICAS",
    "DEPARTAMENTO DE CIÊNCIAS CONTÁBEIS - DCC",
    "DEPARTAMENTO DE CIÊNCIAS SOCIAIS",
    "DEPARTAMENTO DE COMPUTAÇÃO E TECNOLOGIA",
    "DEPARTAMENTO DE COMUNICAÇÃO SOCIAL",
    "DEPARTAMENTO DE DEMOGRAFIA E CIENCIAS ATUARIAIS",
    "DEPARTAMENTO DE DESIGN",
    "DEPARTAMENTO DE DIREITO - CERES",
    "DEPARTAMENTO DE DIREITO PRIVADO - DIPRI",
    "DEPARTAMENTO DE DIREITO PROCESSUAL E PROPEDÊUTICA - DEPRO",
    "DEPARTAMENTO DE DIREITO PUBLICO - DIPUB",
    "DEPARTAMENTO DE ECOLOGIA",
    "DEPARTAMENTO DE ECONOMIA - DEPEC",
    "DEPARTAMENTO DE EDUCAÇÃO - CERES",
    "DEPARTAMENTO DE EDUCAÇÃO FÍSICA",
    "DEPARTAMENTO DE ENFERMAGEM",
    "DEPARTAMENTO DE ENGENHARIA BIOMEDICA",
    "DEPARTAMENTO DE ENGENHARIA CIVIL E AMBIENTAL",
    "DEPARTAMENTO DE ENGENHARIA DE COMPUTACAO E AUTOMACAO",
    "DEPARTAMENTO DE ENGENHARIA DE COMUNICAÇÕES",
    "DEPARTAMENTO DE ENGENHARIA DE MATERIAIS",
    "DEPARTAMENTO DE ENGENHARIA DE PETROLEO",
    "DEPARTAMENTO DE ENGENHARIA ELÉTRICA",
    "DEPARTAMENTO DE ENGENHARIA MECANICA",
    "DEPARTAMENTO DE ENGENHARIA PRODUÇÃO",
    "DEPARTAMENTO DE ENGENHARIA QUIMICA",
    "DEPARTAMENTO DE ENGENHARIA TÊXTIL",
    "DEPARTAMENTO DE ESTATISTICA",
    "DEPARTAMENTO DE FARMACIA",
    "DEPARTAMENTO DE FILOSOFIA",
    "DEPARTAMENTO DE FISIOLOGIA E COMPORTAMENTO",
    "DEPARTAMENTO DE FISIOTERAPIA",
    "DEPARTAMENTO DE FONOAUDIOLOGIA",
    "DEPARTAMENTO DE FUNDAMENTOS E POLÍTICAS DA EDUCAÇÃO",
    "DEPARTAMENTO DE FÍSICA TEÓRICA E EXPERIMENTAL",
    "DEPARTAMENTO DE GEOFÍSICA",
    "DEPARTAMENTO DE GEOGRAFIA/CCHLA",
    "DEPARTAMENTO DE GEOLOGIA",
    "DEPARTAMENTO DE HISTÓRIA",
    "DEPARTAMENTO DE INFECTOLOGIA",
    "DEPARTAMENTO DE INFORMÁTICA E MATEMÁTICA APLICADA",
    "DEPARTAMENTO DE LETRAS",
    "DEPARTAMENTO DE LINGUAS E LITERATURAS ESTRANGEIRAS MODERNAS",
    "DEPARTAMENTO DE MATEMÁTICA",
    "DEPARTAMENTO DE MEDICINA CLÍNICA",
    "DEPARTAMENTO DE MEDICINA INTEGRADA",
    "DEPARTAMENTO DE MICROBIOLOGIA E PARASITOLOGIA",
    "DEPARTAMENTO DE MORFOLOGIA",
    "DEPARTAMENTO DE NUTRIÇÃO",
    "DEPARTAMENTO DE OCEANOGRAFIA E LIMNOLOGIA",
    "DEPARTAMENTO DE ODONTOLOGIA",
    "DEPARTAMENTO DE PATOLOGIA",
    "DEPARTAMENTO DE PEDIATRIA",
    "DEPARTAMENTO DE PRÁTICAS EDUCACIONAIS E CURRÍCULO",
    "DEPARTAMENTO DE PSICOLOGIA",
    "DEPARTAMENTO DE SAÚDE COLETIVA",
    "DEPARTAMENTO DE SERVIÇO SOCIAL - DESSO",
    "DEPARTAMENTO DE TOCO-GINECOLOGIA",
    "DEPARTAMENTO DE TURISMO - DETUR",
    "ESCOLA AGRÍCOLA DE JUNDIAÍ",
    "ESCOLA DE CIÊNCIAS E TECNOLOGIA",
    "ESCOLA DE MÚSICA",
    "ESCOLA DE SAÚDE",
    "ESCOLA MULTICAMPI DE CIÊNCIAS MÉDICAS DO RIO GRANDE DO NORTE",
    "FACULDADE DE CIÊNCIAS DA SAÚDE DO TRAIRI - FACISA",
    "FACULDADE DE ENGENHARIA, LETRAS E CIÊNCIAS SOCIAIS DO SERIDÓ - FELCS",
    "INSTITUTO DE POLÍTICAS PÚBLICAS",
    "INSTITUTO DE QUÍMICA",
    "INSTITUTO DO CÉREBRO",
    "INSTITUTO HUMANITAS DE ESTUDOS INTEGRADOS",
    "INSTITUTO METROPOLE DIGITAL",
  ],

  bannerFiltering: (
    bannerList: BannerItemType[],
    dimissIdList: string[],
    screen?: BannerScreensAvailableType
  ) => {
    const dateNow = new Date().getTime();

    return bannerList.filter((item: BannerItemType) => {
      if (dateNow < item.active.start || dateNow > item.active.end) return false;
      return (screen === undefined || item.screen === screen) && !dimissIdList.includes(item.id);
    });
  },

  convertFileToBase64: async (fileObject: File) => {
    return new Promise<string>((Ok, Err) => {
      try {
        const Reader = new FileReader();

        Reader.onloadend = async (readEvent) =>
          Ok((readEvent.target?.result as string) || (Reader.result as string));

        Reader.readAsDataURL(fileObject);
      } catch (error) {
        console.log(...new Dev("convertFileToBase64", error).Error());
        Err(error);
      }
    });
  },
};

export default UtilFunctions;
